import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, Plus, Trash2, Pencil, Package, X, Save } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface SupplierArticle {
    id: string;
    label: string;
    price_ttc: number;
    vat_rate: number;
    category_id: string | null;
}

interface SupplierCategory {
    id: string;
    name: string;
}

interface Supplier {
    id: string;
    name: string;
    articles: SupplierArticle[];
    categories: SupplierCategory[];
}

const VAT_RATES = [0, 5.5, 10, 20];
const UNCATEGORIZED = 'none';

function ArticleForm({ initialLabel = '', initialPrice = '', initialVat = 20, onSubmit, onCancel }: {
    initialLabel?: string; initialPrice?: string; initialVat?: number;
    onSubmit: (label: string, price: number, vat: number) => Promise<void>; onCancel: () => void;
}) {
    const [label, setLabel] = React.useState(initialLabel);
    const [price, setPrice] = React.useState(initialPrice);
    const [vat, setVat] = React.useState(initialVat);
    const [busy, setBusy] = React.useState(false);

    const submit = async () => {
        if (!label.trim()) return;
        setBusy(true);
        try {
            await onSubmit(label.trim(), Number(price) || 0, vat);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Chocolat noir 1kg" autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
                className="flex-1 min-w-[140px] bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
            <input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Prix"
                className="w-20 shrink-0 bg-surface border border-border rounded-md px-2 py-2 text-sm text-content outline-none focus:border-brand-500 text-right" />
            <select value={vat} onChange={(e) => setVat(Number(e.target.value))}
                className="shrink-0 bg-surface border border-border rounded-md px-1.5 py-2 text-sm text-content outline-none">
                {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
            </select>
            <Button size="sm" onClick={submit} disabled={busy || !label.trim()} className="shrink-0">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            </Button>
            <Button size="sm" variant="secondary" onClick={onCancel} className="shrink-0"><X size={13} /></Button>
        </div>
    );
}

export const SupplierCategoryArticlesPage: React.FC = () => {
    const { id, categoryId } = useParams<{ id: string; categoryId: string }>();
    const navigate = useNavigate();

    const [supplier, setSupplier] = React.useState<Supplier | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [adding, setAdding] = React.useState(false);
    const [editingArticleId, setEditingArticleId] = React.useState<string | null>(null);
    const [renaming, setRenaming] = React.useState(false);
    const [renameValue, setRenameValue] = React.useState('');
    const [savingRename, setSavingRename] = React.useState(false);
    const [error, setError] = React.useState('');

    const load = React.useCallback(() => {
        if (!id) return;
        moduleApi.getSupplier(id).then((s: any) => setSupplier(s || null)).catch(() => setSupplier(null)).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!supplier || !categoryId) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Fournisseur introuvable.</p>
                <Button variant="secondary" onClick={() => navigate('/fournisseurs')}><ArrowLeft size={15} /> Retour</Button>
            </div>
        );
    }

    const isUncategorized = categoryId === UNCATEGORIZED;
    const category = isUncategorized ? { id: UNCATEGORIZED, name: 'Sans catégorie' } : supplier.categories.find((c) => c.id === categoryId);
    if (!category) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Catégorie introuvable.</p>
                <Button variant="secondary" onClick={() => navigate(`/fournisseurs/${id}/articles`)}><ArrowLeft size={15} /> Retour au catalogue</Button>
            </div>
        );
    }

    const articles = supplier.articles.filter((a) => isUncategorized ? !a.category_id : a.category_id === categoryId);
    const qLower = q.trim().toLowerCase();
    const filteredArticles = articles.filter((a) => !qLower || a.label.toLowerCase().includes(qLower));

    const handleAddArticle = async (label: string, price: number, vat: number) => {
        if (!id) return;
        try {
            await moduleApi.createSupplierArticle(id, { label, price_ttc: price, vat_rate: vat, category_id: isUncategorized ? null : categoryId });
            setAdding(false);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible d'ajouter cet article.");
        }
    };

    const handleUpdateArticle = async (articleId: string, label: string, price: number, vat: number) => {
        if (!id) return;
        try {
            await moduleApi.updateSupplierArticle(id, articleId, { label, price_ttc: price, vat_rate: vat, category_id: isUncategorized ? null : categoryId });
            setEditingArticleId(null);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible de modifier cet article.");
        }
    };

    const handleRemoveArticle = async (articleId: string) => {
        if (!id) return;
        try {
            await moduleApi.deleteSupplierArticle(id, articleId);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible de retirer cet article.");
        }
    };

    const handleRenameCategory = async () => {
        if (!id || isUncategorized || !renameValue.trim()) return;
        setSavingRename(true);
        try {
            await moduleApi.updateSupplierCategory(id, categoryId, { name: renameValue.trim() });
            setRenaming(false);
            load();
        } catch (e: any) {
            setError(e?.message || 'Impossible de renommer cette catégorie.');
        } finally {
            setSavingRename(false);
        }
    };

    const handleDeleteCategory = async () => {
        if (!id || isUncategorized) return;
        if (!confirm(`Supprimer la catégorie "${category.name}" ? Ses articles seront conservés, simplement repassés "sans catégorie".`)) return;
        try {
            await moduleApi.deleteSupplierCategory(id, categoryId);
            navigate(`/fournisseurs/${id}/articles`);
        } catch (e: any) {
            setError(e?.message || 'Impossible de supprimer cette catégorie.');
        }
    };

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <button onClick={() => navigate(`/fournisseurs/${id}/articles`)} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium mb-2">
                    <ArrowLeft size={15} /> Catalogue d'articles
                </button>
                {renaming ? (
                    <div className="flex items-center gap-1.5 max-w-sm">
                        <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCategory(); if (e.key === 'Escape') setRenaming(false); }}
                            className="flex-1 min-w-0 bg-surface border border-border rounded-md px-2.5 py-1.5 text-lg font-bold text-content outline-none focus:border-brand-500" />
                        <Button size="sm" onClick={handleRenameCategory} disabled={savingRename || !renameValue.trim()}>
                            {savingRename ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setRenaming(false)}><X size={13} /></Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><Package size={18} /> {category.name}</h1>
                        {!isUncategorized && (
                            <>
                                <button onClick={() => { setRenaming(true); setRenameValue(category.name); }} className="text-content-muted hover:text-content"><Pencil size={14} /></button>
                                <button onClick={handleDeleteCategory} className="text-red-400 hover:text-red-500"><Trash2 size={14} /></button>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un article..." className="pl-8" />
                </div>
                {!adding && (
                    <Button size="sm" onClick={() => setAdding(true)} className="shrink-0">
                        <Plus size={14} /> Ajouter un article
                    </Button>
                )}
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            {adding && (
                <div className="bg-surface rounded-xl border border-border px-4 py-3">
                    <ArticleForm onSubmit={handleAddArticle} onCancel={() => setAdding(false)} />
                </div>
            )}

            <div className="space-y-2">
                {filteredArticles.length === 0 ? (
                    <p className="text-sm text-content-muted">Aucun article ne correspond.</p>
                ) : filteredArticles.map((a) => (
                    <div key={a.id} className="bg-surface rounded-xl border border-border px-4 py-3">
                        {editingArticleId === a.id ? (
                            <ArticleForm
                                initialLabel={a.label} initialPrice={String(a.price_ttc)} initialVat={a.vat_rate}
                                onSubmit={(label, price, vat) => handleUpdateArticle(a.id, label, price, vat)}
                                onCancel={() => setEditingArticleId(null)}
                            />
                        ) : (
                            <div className="flex items-center justify-between gap-3">
                                <span className="flex-1 min-w-0 truncate text-sm text-content">{a.label}</span>
                                <span className="text-xs text-content-muted shrink-0">{a.price_ttc.toFixed(2)} € TTC · {a.vat_rate}%</span>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => setEditingArticleId(a.id)} className="text-content-muted hover:text-content"><Pencil size={13} /></button>
                                    <button onClick={() => handleRemoveArticle(a.id)} className="text-red-400 hover:text-red-500"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
